import type { Document, Resource } from './types'

/**
 * Reading a JSON:API document: the resources it carries in `included`, and
 * the relationships on a resource that point at them or at other records.
 */

/**
 * The first included resource of a type. Matched by type, never by position —
 * the API makes no ordering guarantee about the `included` array.
 */
export function findIncluded(document: Document<unknown>, type: string): Resource | undefined {
  return document.included?.find((resource) => resource.type === type)
}

/** The id a to-one relationship points at, or undefined for an absent or to-many one. */
export function relatedId(resource: Resource, name: string): string | undefined {
  const identifier = resource.relationships?.[name]?.data
  return identifier && !Array.isArray(identifier) ? identifier.id : undefined
}

/** The ids a to-many relationship points at; none for an absent or to-one one. */
export function relatedIds(resource: Resource, name: string): string[] {
  const identifiers = resource.relationships?.[name]?.data
  return Array.isArray(identifiers) ? identifiers.map((identifier) => identifier.id) : []
}

/** Follow a to-one relationship into the document's `included` resources. */
export function relatedResource(
  document: Document<unknown>,
  resource: Resource,
  name: string,
): Resource | undefined {
  const identifier = resource.relationships?.[name]?.data
  if (!identifier || Array.isArray(identifier)) return undefined
  return document.included?.find(
    (candidate) => candidate.type === identifier.type && candidate.id === identifier.id,
  )
}
